import Swiper from 'swiper';
import { scrollTo } from '../../../../application/typescript/utils/utils';

const swiperOptions = {
  direction: 'horizontal' as const,
  slidesPerView: 1,
  spaceBetween: 20,
  grabCursor: true,
  mousewheel: true,
  preventInteractionOnTransition: true,
  breakpoints: {
    640: {
      slidesPerView: 2,
      spaceBetween: 24,
    },
    1025: {
      slidesPerView: 3,
      spaceBetween: 32,
    },
  },
};

/** Aligné sur `animation.ts` (stagger 300 ms) et `_animation.scss` (transition 0.6s). */
const GROUP_STAGGER_MS = 300;
const GROUP_TRANSITION_MS = 600;
const RATE_ANIMATION_BUFFER_MS = 200;

/** Aligné sur `content-sticky-columns__heading` sticky (`top: 150px`). */
const FORMATION_LIST_SCROLL_OFFSET = 150;

/** Légèrement au-dessus du header seul pour le formulaire contact. */
const FORMATION_CONTACT_SCROLL_OFFSET = 120;

class CornerFormation {
  constructor() {
    const testimonySwiper = document.querySelector('.corner-formation__testimony-swiper');
    const actualitiesSwiper = document.querySelector('.corner-formation__actualities-swiper');

    if (testimonySwiper) {
      new Swiper('.corner-formation__testimony-swiper', swiperOptions);
    }

    if (actualitiesSwiper) {
      new Swiper('.corner-formation__actualities-swiper', swiperOptions);
    }

    this.initHeroAnchorScroll();
    this.initQualiopiRates();
  }

  private initHeroAnchorScroll(): void {
    const anchors = document.querySelectorAll<HTMLAnchorElement>(
      '.formation-hero__actions a[href^="#"]',
    );

    anchors.forEach((anchor) => {
      anchor.addEventListener('click', (event) => {
        const href = anchor.getAttribute('href');

        if (!href || href === '#') {
          return;
        }

        const target = document.querySelector(href);

        if (!target) {
          return;
        }

        event.preventDefault();

        const header = document.querySelector('.header') as HTMLElement | null;
        const headerHeight = header?.offsetHeight ?? 0;
        const scrollOffset =
          href === '#corner-formation-list'
            ? FORMATION_LIST_SCROLL_OFFSET
            : href === '#formation-contact'
              ? FORMATION_CONTACT_SCROLL_OFFSET
              : headerHeight;
        const extraMargin = Math.max(0, scrollOffset - headerHeight);

        scrollTo(target, extraMargin);
      });
    });
  }

  private getQualiopiRevealDelay(element: HTMLElement): number {
    const qualiopi = element.closest('.formation-hero__qualiopi');
    const group = element.closest('[data-animation^="group-"]');

    if (!qualiopi || !group) {
      return GROUP_TRANSITION_MS + RATE_ANIMATION_BUFFER_MS;
    }

    const index = Array.from(group.children).indexOf(qualiopi);

    if (index < 0) {
      return GROUP_TRANSITION_MS + RATE_ANIMATION_BUFFER_MS;
    }

    return index * GROUP_STAGGER_MS + GROUP_TRANSITION_MS + RATE_ANIMATION_BUFFER_MS;
  }

  private runAfterGroupReveal(element: HTMLElement, callback: () => void): void {
    const group = element.closest('[data-animation^="group-"]') as HTMLElement | null;

    const schedule = () => {
      window.setTimeout(callback, this.getQualiopiRevealDelay(element));
    };

    if (!group || group.classList.contains('is-visible')) {
      schedule();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!group.classList.contains('is-visible')) {
        return;
      }

      observer.disconnect();
      schedule();
    });

    observer.observe(group, { attributes: true, attributeFilter: ['class'] });
  }

  private initQualiopiRates(): void {
    const qualiopi = document.querySelector('.formation-hero__qualiopi');

    if (!qualiopi) {
      return;
    }

    const rateValues = qualiopi.querySelectorAll<HTMLElement>('[data-rate-value]');

    if (!rateValues.length) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const animateRate = (element: HTMLElement) => {
      if (element.dataset.rateAnimated === 'true') {
        return;
      }

      element.dataset.rateAnimated = 'true';

      const target = Number.parseFloat(element.dataset.rateValue || '0');

      if (Number.isNaN(target)) {
        return;
      }

      const formattedTarget = `${Math.round(target)}%`;

      if (prefersReducedMotion) {
        element.textContent = formattedTarget;
        return;
      }

      element.classList.add('is-animating');

      const duration = 1800;
      const startTime = performance.now();

      const step = (time: number) => {
        const progress = Math.min((time - startTime) / duration, 1);
        const eased = 1 - (1 - progress) ** 3;
        const current = Math.round(target * eased);

        element.textContent = `${current}%`;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          element.textContent = formattedTarget;
        }
      };

      requestAnimationFrame(step);
    };

    let ratesAnimationStarted = false;

    const startRatesAnimation = () => {
      if (ratesAnimationStarted) {
        return;
      }

      ratesAnimationStarted = true;

      const firstRate = rateValues[0] as HTMLElement;

      this.runAfterGroupReveal(firstRate, () => {
        rateValues.forEach((element) => animateRate(element));
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          startRatesAnimation();
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 },
    );

    observer.observe(qualiopi);
  }
}

export const cornerFormation = new CornerFormation();
